import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

type Pos = { left: number; top?: number; bottom?: number };

// Room the card needs below its trigger before it flips above instead.
const FLIP_MARGIN = 200;

/**
 * Shows `content` in a small floating card while the trigger is hovered or
 * keyboard-focused. Rendered into document.body with fixed positioning so it
 * isn't clipped by `overflow-hidden` containers (e.g. the ticket list table).
 * Dismisses on mouse-out, blur, Escape, or scroll.
 */
export function HoverCard({ content, children }: { content: ReactNode; children: ReactNode }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  const id = useId();

  function show() {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - 328));
    setPos(
      r.bottom + FLIP_MARGIN > window.innerHeight
        ? { left, bottom: window.innerHeight - r.top + 6 }
        : { left, top: r.bottom + 6 },
    );
  }
  const hide = () => setPos(null);

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && hide();
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', hide, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide, true);
    };
  }, [pos]);

  return (
    <>
      <span
        ref={ref}
        tabIndex={0}
        aria-describedby={pos ? id : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className="inline-flex cursor-help rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        {children}
      </span>
      {pos &&
        createPortal(
          <div
            id={id}
            role="tooltip"
            style={{ position: 'fixed', ...pos }}
            className="z-50 w-80 max-w-[calc(100vw-16px)] rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 shadow-lg pointer-events-none"
          >
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
